// Fetchers for the admin Dashboard page (live warehouse activity).
// Separate from adminDashboard.ts, which serves the /admin homepage
// KPI cards — these hit the dedicated /admin/dashboard/* aggregates.
// Each endpoint returns { live, completed }: what's happening right
// now plus the same work finished today (warehouse time).

import { apiFetch } from "./apiFetch";

/** Speed/ETA fields shared by both live tables. */
export interface LiveProgress {
    total_items: number;
    pct: number;
    /** Items per minute over the recent scan window; null before enough scans. */
    speed_ipm: number | null;
    eta_minutes: number | null;
    /** ISO timestamp of the projected finish; null when speed is unknown. */
    eta_at: string | null;
    last_scan_at: string | null;
    /** No scan in the last 5 minutes (or none yet). */
    idle: boolean;
    /** The current holder is not the person who started this wave. */
    took_over: boolean;
}

interface PickRowBase {
    wave_id: number;
    warehouse: string;
    pick_type: "light" | "pick_and_sort" | "large";
    /** Which wall a Pick & Sort wave sorts at; 'put_wall' = a Putwall wave. */
    sort_station?: "pick_and_sort" | "put_wall";
    tier: "high" | "low" | null;
    picker_uid: number | null;
    picker_name: string | null;
    /** Live: when the current holder took the wave. Completed: when this picker's stint began. */
    started_at: string | null;
    total_items: number;
    picked_items: number;
    /** Current/last picked location (display name), from the latest location-bearing scan. */
    location: string | null;
    /**
     * Large waves only (empty otherwise): every order the wave has held,
     * in the order they were appended. The Waves tab shows these instead
     * of the wave id — a Large wave is one order at a time.
     */
    order_ids: string[];
}

export interface LivePickRow extends PickRowBase, LiveProgress {
    status: "active" | "paused";
    /** Large only: the order being picked now (or the last one appended). */
    current_order_id: string | null;
}

/**
 * One picker's stint on a wave. A wave picked by one person start to
 * finish is one row; a handed-over wave is one row per picker, the
 * earlier ones flagged handed_over. Speeds count active picking time
 * only — paused gaps are excluded.
 */
export interface CompletedPickRow extends PickRowBase {
    /** When this picker's stint ended: their pause (handed over) or the wave completion. */
    ended_at: string;
    /** This picker was succeeded by someone else before the wave finished. */
    handed_over: boolean;
    /** This picker was not the one who started the wave. */
    took_over: boolean;
    pct: number;
    speed_ipm: number | null;
}

interface SortRowBase {
    wave_id: number;
    warehouse: string;
    pick_type: string;
    sorter_uid: number | null;
    sorter_name: string | null;
    bay_names: string[];
    entered_at: string | null;
    total_items: number;
    sorted_items: number;
    orders_total: number;
    orders_sorted: number;
}

export interface LiveSortRow extends SortRowBase, LiveProgress {}

/**
 * One sorter's stint on a wave — same attribution model as picking:
 * a handed-over wave is one row per sorter, earlier ones flagged, and
 * speeds count active sorting spans only.
 */
export interface CompletedSortRow extends SortRowBase {
    /** When this sorter's stint ended: their last activity (handed over) or the sort finish. */
    ended_at: string;
    handed_over: boolean;
    /** This sorter was not the one who started the sort. */
    took_over: boolean;
    pct: number;
    speed_ipm: number | null;
}

/**
 * Individual view: one line per person (per pick type for picking)
 * aggregated over the day's LIVE + COMPLETED rows — the full set
 * server-side, not the 200-row window the tables show. `minutes` is the same basis
 * the row speeds use (active scanning time for picking/sorting,
 * session spans elsewhere); speed_ipm = units ÷ minutes (FE ×60).
 */
interface ByUserBase {
    user_uid: number | string | null;
    user_name: string | null;
    units: number;
    minutes: number;
}
export interface PickingByUser extends ByUserBase {
    pick_type: "light" | "pick_and_sort" | "large";
    waves: number;
    speed_ipm: number | null;
}
export interface SortingByUser extends ByUserBase {
    waves: number;
    speed_ipm: number | null;
}
export interface GoodsInByUser extends ByUserBase {
    sessions: number;
    /** Book In totes completed (BF Scan sessions count in `sessions` only). */
    totes: number;
    speed_ipm: number | null;
}
export interface ShelfDropByUser extends ByUserBase {
    sessions: number;
    speed_ipm: number | null;
}
export interface DispatchByUser extends ByUserBase {
    /** Picking type of the orders packed; null = never waved (manual / bulk / auto-ship). */
    pick_type: DispatchPickType;
    sessions: number;
    orders: number;
    parcels: number;
    orders_ph: number | null;
    units_ph: number | null;
    parcels_ph: number | null;
}

export interface PickingActivity {
    live: LivePickRow[];
    completed: CompletedPickRow[];
    by_user: PickingByUser[];
}

export interface SortingActivity {
    live: LiveSortRow[];
    completed: CompletedSortRow[];
    by_user: SortingByUser[];
}

/**
 * Sorter 1 / Sorter 2 (Newport, Poland — 2026-10-20): one row per
 * date+batch the station worked in the window. Progress (done / total)
 * is the batch as a whole, from the sorter's own item records; activity
 * (items_in_window, minutes, speed, people) is what happened inside the
 * window. Sorter 1 runs one batch at a time; Sorter 2 several.
 */
export interface SorterBatchRow {
    sorter: 1 | 2;
    warehouse: string;
    /** YYYY-MM-DD batch date; null when the batch could not be named. */
    date: string | null;
    batch: number | null;
    items_total: number;
    items_done: number;
    pct: number;
    items_in_window: number;
    /** First scan the compute saw on the batch (may precede the window). */
    started_at: string | null;
    /** First scan inside the window — today's start of work on the batch. */
    window_started_at: string | null;
    last_scan_at: string | null;
    /** Set once the batch is finished or the station has left it. */
    ended_at: string | null;
    finished: boolean;
    people: number;
    minutes: number;
    speed_ipm: number | null;
}
export interface SorterByUser extends ByUserBase {
    sessions: number;
    speed_ipm: number | null;
}
export interface SorterTeam {
    items: number;
    active_minutes: number;
    items_per_hour: number | null;
    /** Everyone who scanned in the window. */
    people: number;
    /** Scanning in the last 15 minutes — on the station right now (today only). */
    people_now: number;
}
export interface SorterActivity {
    live: SorterBatchRow[];
    completed: SorterBatchRow[];
    by_user: SorterByUser[];
    team: SorterTeam;
}

/** One slice of a sorter's scan history; age 0 = the most recent window. */
export interface SorterRaceBucket {
    age: number;
    items: number;
}
/**
 * The sorter race block: the headline batch (Sorter 1's active one;
 * Sorter 2's oldest unfinished) with its target finish — today's first
 * scan on it + the items left at that point ÷ the station's 30-day
 * average — the pace slices, and the other live batches.
 */
export interface SorterRace extends SorterBatchRow {
    target_at: string | null;
    items_left_at_start: number;
    /** "person": per-person 30-day rate × bays (a full team); "station": the station's own rate (Newport's conveyor). */
    target_basis: "person" | "station";
    /** Sorting bays on the station: the target assumes one person per bay for the whole sort. */
    bays: number;
    /** Scanning in the last 15 minutes. */
    people_now: number;
    avg_items_per_hour: number | null;
    avg_person_items_per_hour: number | null;
    avg_person_days: number;
    avg_days: number;
    avg_window_days: number;
    window_minutes: number;
    history_minutes: number;
    buckets: SorterRaceBucket[];
    others: SorterBatchRow[];
    generated_at: string;
}

/** Pallets Created (Newport, Poland): one row per pallet made in the window. */
export type PalletColour = "RED" | "YELLOW" | "GREY" | "BLACK";
export interface PalletCreatedRow {
    pallet_sku: string;
    warehouse: string;
    colour: PalletColour;
    status: string;
    pallet_type: string | null;
    size_name: string | null;
    height_cm: number | null;
    user_uid: number | null;
    user_name: string | null;
    created_at: string;
}
export interface PalletsByUser {
    user_uid: number | string | null;
    user_name: string | null;
    pallets: number;
    red: number;
    yellow: number;
    grey: number;
    black: number;
}
export interface PalletsTeam {
    pallets: number;
    red: number;
    yellow: number;
    grey: number;
    black: number;
}
export interface PalletsCreatedActivity {
    live: PalletCreatedRow[];
    completed: PalletCreatedRow[];
    by_user: PalletsByUser[];
    team: PalletsTeam;
}

/**
 * One Goods In session: a Book In tote (open → complete) or a BF Scan
 * Sheet session. Aggregate throughput — units booked and units/hr —
 * never per-order rows. Move Stock rebooks are excluded server-side.
 * A tote / session with a break of over 15 minutes between scans comes
 * back as one row per stint, all under the same ref.
 */
interface GoodsInRowBase {
    /** sourcing = the scan sheet in Goods In › Sourcing mode, its own activity. */
    kind: "book_in" | "bf_scan" | "sourcing";
    /** bf_scan only: which scan sheet variant the session ran on. */
    bf_mode?: "standard" | "big";
    /** Tote id (book_in) or "#<session_id>" (bf_scan). */
    ref: string;
    warehouse: string | null;
    user_uid: number | string | null;
    user_name: string | null;
    units: number;
    started_at: string | null;
    /** Items per minute over the session's scan span (FE renders ×60). */
    speed_ipm: number | null;
}

export interface LiveGoodsInRow extends GoodsInRowBase {
    last_scan_at: string | null;
    idle: boolean;
}

export interface CompletedGoodsInRow extends GoodsInRowBase {
    ended_at: string;
}

/**
 * One shelf dropper. Everyone works the same shared replenishment
 * list, so there is no quantity target — rows carry units picked into
 * the tote (put-away is the tail of the same journey, never counted
 * twice) plus units/hr and the person's latest recorded location.
 */
interface ShelfDropRowBase {
    picker_uid: number | null;
    picker_name: string | null;
    warehouse: string | null;
    started_at: string | null;
    units: number;
    /** Items per minute (FE renders ×60 as items/hr). Picking time
     *  only — put-away walks are excluded: both live and completed
     *  rows carry the stint's per-session picking-time aggregate. */
    speed_ipm: number | null;
    /** Latest location the person acted at (display name) — source shelf while picking, target pick face while putting away. */
    location: string | null;
}

export interface LiveShelfDropRow extends ShelfDropRowBase {
    /** Whether their latest action was a pick scan or a put-away. */
    phase: "picking" | "putting_away";
    last_activity_at: string | null;
    idle: boolean;
}

export interface CompletedShelfDropRow extends ShelfDropRowBase {
    ended_at: string;
    abandoned: boolean;
}

export interface ShelfDropActivity {
    live: LiveShelfDropRow[];
    completed: CompletedShelfDropRow[];
    by_user: ShelfDropByUser[];
}

/** Picking type of a packed order; null = never went through a wave. */
export type DispatchPickType = "light" | "pick_and_sort" | "large" | null;

/**
 * One dispatch packer session — their packed_at stream split on
 * 45-minute gaps and on a change of pick type. Orders, units and
 * parcels each carry a per-hour rate computed over the session span.
 */
interface DispatchRowBase {
    user_uid: number | null;
    user_name: string | null;
    warehouse: string | null;
    pick_type: DispatchPickType;
    started_at: string;
    orders: number;
    units: number;
    parcels: number;
    orders_ph: number | null;
    units_ph: number | null;
    parcels_ph: number | null;
}

export interface LiveDispatchRow extends DispatchRowBase {
    last_packed_at: string;
    idle: boolean;
}

export interface CompletedDispatchRow extends DispatchRowBase {
    ended_at: string;
}

export interface GoodsInActivity {
    live: LiveGoodsInRow[];
    completed: CompletedGoodsInRow[];
    by_user: GoodsInByUser[];
}

/**
 * Put Away: one session per person per destination TYPE — hot restock
 * (low locations), cold restock (high locations), other (unclassified).
 * Cold rates are totes/hr, hot/other rates items/hr (`rate_ph` is
 * already in the row's own unit). `misslotted` counts tote-level
 * put-aways whose tote category disagrees with the tier it went to.
 */
export type PutAwayType = "hot" | "cold" | "other";
interface PutAwayRowBase {
    user_uid: number | null;
    user_name: string | null;
    warehouse: string | null;
    type: PutAwayType;
    totes: number;
    units: number;
    misslotted: number;
    rate_ph: number | null;
    started_at: string;
}
export interface LivePutAwayRow extends PutAwayRowBase {
    last_put_away_at: string;
    idle: boolean;
}
export interface CompletedPutAwayRow extends PutAwayRowBase {
    ended_at: string;
}
export interface PutAwayByUser {
    user_uid: number | null;
    user_name: string | null;
    type: PutAwayType;
    sessions: number;
    totes: number;
    units: number;
    misslotted: number;
    minutes: number;
    rate_ph: number | null;
}
export interface PutAwayActivity {
    live: LivePutAwayRow[];
    completed: CompletedPutAwayRow[];
    by_user: PutAwayByUser[];
}

export interface DispatchActivity {
    live: LiveDispatchRow[];
    completed: CompletedDispatchRow[];
    by_user: DispatchByUser[];
}

/**
 * Team view: one payload of per-department summary numbers for today.
 * Every units/hr is total units ÷ total session hours (the same
 * session/stint bases the individual rows use), already per-hour.
 * Hot/cold = the BF Scan HOT/COLD classification.
 *
 * Each department also carries an hourly activity histogram over the
 * site's scanning day (site.day_start_hour → site.day_end_hour, one
 * bucket per hour — the same axis for every department so the charts
 * align), plus a same-point-yesterday total for the "vs yesterday"
 * deltas. The hourly figures come from the raw scan streams, so they
 * can differ by a hair from the session-based headline totals.
 */
/** ISO instant the BE snapshot behind a Team payload was computed (see "Updated Ns ago"). */
export interface Snapshotted {
    snapshot_at?: string;
}

export interface TeamSite {
    /** Orders shipped today that were placed before the cut-off. */
    orders_dispatched: number;
    /** Shipped today + still open, placed before cut-off — grows until cut-off, then freezes. */
    orders_due: number;
    orders_dispatched_same_time_yesterday: number;
    /** Express (express + priority delivery) / standard halves of the two figures above — they sum back to them. */
    orders_dispatched_express: number;
    orders_due_express: number;
    orders_dispatched_standard: number;
    orders_due_standard: number;
    /** "13:00" — London wall clock, from BE config. */
    cutoff: string;
    /** "15:30" — dispatch cut-off: shipped after this on the due day = late. */
    dispatch_cutoff: string;
    /** Chart axis, London hours: [day_start_hour, day_end_hour). */
    day_start_hour: number;
    day_end_hour: number;
    /** The London hour currently in progress (colours the live bar). */
    current_hour: number;
    generated_at: string;
    /** Which Team layout the BE built the payload for (absent from older snapshots). */
    layout?: "picking" | "sorter";
}

/** A sorter station's Team card + race (sorter layout only). */
export interface TeamSorter {
    items: number;
    items_per_hour: number | null;
    /** Everyone who scanned today. */
    people: number;
    /** Scanning in the last 15 minutes. */
    people_now: number;
    batches_finished: number;
    hourly: number[];
    yesterday_items: number;
    /** null = nothing scanned on the station today. */
    current: SorterRace | null;
}
export interface TeamPalletsCreated extends PalletsTeam {
    hourly: number[];
    yesterday_pallets: number;
}

/** One slice of packing history; age 0 = the most recent `window_minutes`. */
export interface TeamRaceBucket {
    age: number;
    items: number;
    orders: number;
}

export interface TeamRace {
    /** Slice length in minutes. */
    window_minutes: number;
    /** How far back the slices reach (history_minutes / window_minutes slices). */
    history_minutes: number;
    items_due: number;
    items_done: number;
    orders_due: number;
    orders_done: number;
    /** Newest slice only (= buckets[0]); kept for the pre-smoothing shape. */
    items_packed_window: number;
    orders_packed_window: number;
    /** Zero-filled, newest first. The FE weights these into the pace. */
    buckets: TeamRaceBucket[];
    /** Today's first pack (ISO) — slices before it carry no weight; null before any pack. */
    first_pack_at: string | null;
    /** Today's 06:00 London as an instant (ISO): where the time lorry starts. */
    day_start_at: string | null;
    /** Last pre-cut-off pack of a due order (ISO) — the actual finish time once items_done === items_due. */
    last_pack_at: string | null;
    /** Today's dispatch cut-off as an instant (ISO); null only before the first fetch. */
    cutoff_at: string | null;
    /** "15:30" */
    dispatch_cutoff: string;
    generated_at: string;
}

export interface TeamOverview extends Snapshotted {
    site: TeamSite;
    goods_in: {
        units: number;
        units_per_hour: number | null;
        hot_units: number;
        cold_units: number;
        /** Goods In › Sourcing units today, outside the BF figures above. */
        sourcing_units?: number;
        hourly: number[];
        yesterday_units: number;
    };
    putaway: {
        totes_to_restock: number;
        hot_queue: number;
        cold_queue: number;
        hot_done_today: number;
        cold_done_today: number;
        totes_today: number;
        units_today: number;
        hot_units_today: number;
        cold_units_today: number;
        /** Distinct SKUs put away today / of those, SKUs the warehouse held none of beforehand. */
        skus_today: number;
        new_skus_today: number;
        /** Totes per hour (the headline metric). */
        hourly: number[];
        yesterday_totes: number;
    };
    picking: {
        shelf_drop_units: number;
        shelf_drop_awaiting_putaway: number;
        /** Queue tiles: units still to drop for orders due today/overdue, and closed totes in the put-away queue. */
        shelf_drop_units_due_today: number;
        shelf_drop_totes_awaiting_putaway: number;
        shelf_drop_units_per_hour: number | null;
        items_picked: number;
        items_per_hour: number | null;
        hourly: number[];
        yesterday_items: number;
        shelf_drop_hourly: number[];
        shelf_drop_yesterday_units: number;
    };
    /** Forecast Drop card — the shelf-drop figures' twin over the forecast module. */
    forecast_drop: {
        units: number;
        awaiting_putaway: number;
        /** Queue tiles: the whole current forecast queue (no due filter), and closed totes in the put-away queue. */
        units_to_drop: number;
        totes_awaiting_putaway: number;
        units_per_hour: number | null;
        hourly: number[];
        yesterday_units: number;
    };
    /**
     * "Picking due today" — ITEMS of the same order population as the
     * orders card (out before the ship cut-off + open orders due by today),
     * split by where each order stands. Fulfilled = packed before the ship
     * cut-off (the orders card's "out" rule); packed after it = late. The
     * six stages + items_late sum to items_due.
     */
    picking_due: {
        items_due: number;
        items_unfulfilled: number;
        items_picking: number;
        items_sorting: number;
        items_dispatch: number;
        items_flagged: number;
        items_fulfilled: number;
        items_late: number;
        /** "15:30" */
        dispatch_cutoff: string;
    };
    /**
     * "Ready to pick" ring — units on waiting (unfulfilled, un-waved)
     * orders due exactly today (overdue excluded), bucketed by the whole
     * order (the Orders page's low_ready tick): ready = every line of the
     * order can be picked down low; not_ready = every unit of an order
     * with at least one line up high or short. not_ready is split for the
     * amber/red arcs: low_waiting = already down low, waiting on the
     * order's other lines; high = still up high or short. Older BE
     * snapshots lack the split fields.
     */
    picking_ready: {
        units_ready: number;
        units_not_ready: number;
        units_low_waiting?: number;
        units_high?: number;
        orders_ready: number;
        orders_not_ready: number;
    };
    /**
     * "Race to the cut-off": today's due items and orders vs done, plus the
     * packing throughput of the last `window_minutes` (masters, own lines)
     * that the FE turns into a finish-time estimate.
     */
    race: TeamRace;
    sorting: {
        items_sorted: number;
        orders_sorted: number;
        items_per_hour: number | null;
        hourly: number[];
        yesterday_items: number;
    };
    dispatch: {
        orders: number;
        units: number;
        units_per_hour: number | null;
        hourly: number[];
        yesterday_units: number;
    };
    /** Sorter layout only (Newport, Poland) — absent for Pembroke / all warehouses. */
    sorter_1?: TeamSorter;
    sorter_2?: TeamSorter;
    pallets_created?: TeamPalletsCreated;
}

/**
 * Inclusive London-calendar dates (YYYY-MM-DD) for the activity
 * endpoints' history window. Omitted = today, the live default.
 */
export type ActivityRange = { from: string; to: string };

const fetchActivity = async <T,>(
    path: string,
    warehouse: string,
    extra: Record<string, string> = {},
): Promise<T> => {
    const params = new URLSearchParams(extra);
    if (warehouse) params.set("warehouse", warehouse);
    const qs = params.toString();
    const res = await apiFetch(`${path}${qs ? `?${qs}` : ""}`, {});
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
};

export const EMPTY_PICKING: PickingActivity = { live: [], completed: [], by_user: [] };
export const EMPTY_SHELF_DROP: ShelfDropActivity = { live: [], completed: [], by_user: [] };
export const EMPTY_SORTING: SortingActivity = { live: [], completed: [], by_user: [] };
export const EMPTY_GOODS_IN: GoodsInActivity = { live: [], completed: [], by_user: [] };
export const EMPTY_PUTAWAY: PutAwayActivity = { live: [], completed: [], by_user: [] };
export const EMPTY_DISPATCH: DispatchActivity = { live: [], completed: [], by_user: [] };
export const EMPTY_SORTER: SorterActivity = {
    live: [], completed: [], by_user: [], team: { items: 0, active_minutes: 0, items_per_hour: null, people: 0, people_now: 0 },
};
export const EMPTY_PALLETS_CREATED: PalletsCreatedActivity = {
    live: [], completed: [], by_user: [], team: { pallets: 0, red: 0, yellow: 0, grey: 0, black: 0 },
};

export const fetchPickingActivity = (warehouse: string, range?: ActivityRange): Promise<PickingActivity> =>
    fetchActivity<PickingActivity>("/admin/dashboard/picking", warehouse, range);

export const fetchShelfDropActivity = (warehouse: string, range?: ActivityRange): Promise<ShelfDropActivity> =>
    fetchActivity<ShelfDropActivity>("/admin/dashboard/shelf-drop", warehouse, range);

/** Forecast Drop = the shelf-drop payload shape over the forecast module. */
export const fetchForecastDropActivity = (warehouse: string, range?: ActivityRange): Promise<ShelfDropActivity> =>
    fetchActivity<ShelfDropActivity>("/admin/dashboard/forecast-drop", warehouse, range);

export const fetchSortingActivity = (warehouse: string, range?: ActivityRange): Promise<SortingActivity> =>
    fetchActivity<SortingActivity>("/admin/dashboard/sorting", warehouse, range);

export const fetchGoodsInActivity = (warehouse: string, range?: ActivityRange): Promise<GoodsInActivity> =>
    fetchActivity<GoodsInActivity>("/admin/dashboard/goods-in", warehouse, range);

export const fetchPutAwayActivity = (warehouse: string, range?: ActivityRange): Promise<PutAwayActivity> =>
    fetchActivity<PutAwayActivity>("/admin/dashboard/put-away", warehouse, range);

export const fetchDispatchActivity = (warehouse: string, range?: ActivityRange): Promise<DispatchActivity> =>
    fetchActivity<DispatchActivity>("/admin/dashboard/dispatch", warehouse, range);

export const fetchSorter1Activity = (warehouse: string, range?: ActivityRange): Promise<SorterActivity> =>
    fetchActivity<SorterActivity>("/admin/dashboard/sorter-1", warehouse, range);

export const fetchSorter2Activity = (warehouse: string, range?: ActivityRange): Promise<SorterActivity> =>
    fetchActivity<SorterActivity>("/admin/dashboard/sorter-2", warehouse, range);

export const fetchPalletsCreatedActivity = (warehouse: string, range?: ActivityRange): Promise<PalletsCreatedActivity> =>
    fetchActivity<PalletsCreatedActivity>("/admin/dashboard/pallets-created", warehouse, range);

// Fallback shift window mirroring the BE constants — only seen before
// the first successful fetch.
const EMPTY_HOURLY = Array<number>(11).fill(0);

export const EMPTY_TEAM: TeamOverview = {
    site: {
        orders_dispatched: 0, orders_due: 0,
        orders_dispatched_same_time_yesterday: 0,
        orders_dispatched_express: 0, orders_due_express: 0,
        orders_dispatched_standard: 0, orders_due_standard: 0,
        cutoff: "13:00", dispatch_cutoff: "15:30", day_start_hour: 6, day_end_hour: 17,
        current_hour: -1, generated_at: "",
    },
    goods_in: {
        units: 0, units_per_hour: null, hot_units: 0, cold_units: 0,
        hourly: EMPTY_HOURLY, yesterday_units: 0,
    },
    putaway: {
        totes_to_restock: 0, hot_queue: 0, cold_queue: 0,
        hot_done_today: 0, cold_done_today: 0,
        totes_today: 0, units_today: 0,
        hot_units_today: 0, cold_units_today: 0,
        skus_today: 0, new_skus_today: 0,
        hourly: EMPTY_HOURLY, yesterday_totes: 0,
    },
    picking: {
        shelf_drop_units: 0, shelf_drop_awaiting_putaway: 0,
        shelf_drop_units_due_today: 0, shelf_drop_totes_awaiting_putaway: 0,
        shelf_drop_units_per_hour: null, items_picked: 0, items_per_hour: null,
        hourly: EMPTY_HOURLY, yesterday_items: 0,
        shelf_drop_hourly: EMPTY_HOURLY, shelf_drop_yesterday_units: 0,
    },
    forecast_drop: {
        units: 0, awaiting_putaway: 0, units_to_drop: 0, totes_awaiting_putaway: 0, units_per_hour: null,
        hourly: EMPTY_HOURLY, yesterday_units: 0,
    },
    picking_due: {
        items_due: 0, items_unfulfilled: 0, items_picking: 0, items_sorting: 0,
        items_dispatch: 0, items_flagged: 0, items_fulfilled: 0, items_late: 0,
        dispatch_cutoff: "15:30",
    },
    picking_ready: {
        units_ready: 0, units_not_ready: 0, units_low_waiting: 0, units_high: 0,
        orders_ready: 0, orders_not_ready: 0,
    },
    race: {
        window_minutes: 15, history_minutes: 90, items_due: 0, items_done: 0, orders_due: 0, orders_done: 0,
        items_packed_window: 0, orders_packed_window: 0, buckets: [], first_pack_at: null,
        day_start_at: null, last_pack_at: null, cutoff_at: null,
        dispatch_cutoff: "15:30", generated_at: "",
    },
    sorting: {
        items_sorted: 0, orders_sorted: 0, items_per_hour: null,
        hourly: EMPTY_HOURLY, yesterday_items: 0,
    },
    dispatch: {
        orders: 0, units: 0, units_per_hour: null,
        hourly: EMPTY_HOURLY, yesterday_units: 0,
    },
};

export const fetchTeamOverview = (warehouse: string): Promise<TeamOverview> =>
    fetchActivity<TeamOverview>("/admin/dashboard/team", warehouse);

/**
 * Planner (Alex, 2026-09-21): the staffing calculator's prefilled
 * inputs. `work` = what is still to do for orders due today, by stage
 * and pick type (orders already in a wave carry the wave's type; the
 * rest are classified as the wave gate would). `rates` = units per
 * PERSON-HOUR over the last `rates_window.days` closed days on the
 * Summary view's session bases; null under 30 minutes of history.
 */
export type PlannerPickType = "light" | "pick_and_sort" | "large";
export interface PlannerBucket { orders: number; units: number; }
export interface PlannerRate { units: number; minutes: number; units_ph: number | null; }
export interface PlannerDispatchRate extends PlannerRate { orders: number; orders_ph: number | null; }
export type PlannerScope = "due" | "all";
export interface PlannerPayload extends Snapshotted {
    generated_at: string;
    /** Which orders `work` covers: due today, or every open order. */
    scope: PlannerScope;
    /** "13:00" — orders placed after this are due tomorrow. */
    order_cutoff: string;
    /** "15:30" — due orders must have shipped by this. */
    dispatch_cutoff: string;
    rates_window: { from: string; to: string; days: number };
    work: {
        /**
         * Units still to bring down. AM = the queue for orders placed
         * before `am_cutoff` (06:00), due down by 08:30; PM = the rest,
         * due by 13:00. `units` = both.
         */
        shelf_drop: { units: number; am_units: number; pm_units: number; am_cutoff: string };
        picking: Record<PlannerPickType, PlannerBucket>;
        sorting: { pick_and_sort: PlannerBucket };
        dispatch: Record<PlannerPickType, PlannerBucket>;
        /** Blocked on stock — reported, not planned. */
        flagged: PlannerBucket;
        orders_open: number;
    };
    rates: {
        shelf_drop: PlannerRate;
        picking: Record<PlannerPickType, PlannerRate>;
        sorting: { pick_and_sort: PlannerRate };
        dispatch: Record<PlannerPickType, PlannerDispatchRate>;
    };
    /** Distinct people on each job's live rows right now. */
    active: {
        shelf_drop: number;
        picking: Record<PlannerPickType, number>;
        sorting: { pick_and_sort: number };
        dispatch: Record<PlannerPickType, number>;
    };
}

const zeroBucket = (): PlannerBucket => ({ orders: 0, units: 0 });
const zeroRate = (): PlannerRate => ({ units: 0, minutes: 0, units_ph: null });
const zeroDispatchRate = (): PlannerDispatchRate => ({ ...zeroRate(), orders: 0, orders_ph: null });
const perType = <T,>(make: () => T): Record<PlannerPickType, T> =>
    ({ light: make(), pick_and_sort: make(), large: make() });

export const EMPTY_PLANNER: PlannerPayload = {
    generated_at: "",
    scope: "due",
    order_cutoff: "13:00",
    dispatch_cutoff: "15:30",
    rates_window: { from: "", to: "", days: 7 },
    work: {
        shelf_drop: { units: 0, am_units: 0, pm_units: 0, am_cutoff: "06:00" },
        picking: perType(zeroBucket),
        sorting: { pick_and_sort: zeroBucket() },
        dispatch: perType(zeroBucket),
        flagged: zeroBucket(),
        orders_open: 0,
    },
    rates: {
        shelf_drop: zeroRate(),
        picking: perType(zeroRate),
        sorting: { pick_and_sort: zeroRate() },
        dispatch: perType(zeroDispatchRate),
    },
    active: {
        shelf_drop: 0,
        picking: perType(() => 0),
        sorting: { pick_and_sort: 0 },
        dispatch: perType(() => 0),
    },
};

// A BE build behind the FE (or a snapshot saved by one) can lack a block
// added since — the Team view crashed on exactly that on 2026-09-09 —
// so every block and per-type entry is merged over the zeros.
const mergeTypes = <T extends object>(base: Record<PlannerPickType, T>, over?: Partial<Record<PlannerPickType, T>>) =>
    ({
        light: { ...base.light, ...(over?.light ?? {}) },
        pick_and_sort: { ...base.pick_and_sort, ...(over?.pick_and_sort ?? {}) },
        large: { ...base.large, ...(over?.large ?? {}) },
    });
const mergeCounts = (base: Record<PlannerPickType, number>, over?: Partial<Record<PlannerPickType, number>>) =>
    ({ ...base, ...(over ?? {}) });

export const normalisePlanner = (raw: Partial<PlannerPayload> | null | undefined): PlannerPayload => {
    const e = EMPTY_PLANNER;
    const p = raw ?? {};
    return {
        ...e,
        ...p,
        rates_window: { ...e.rates_window, ...(p.rates_window ?? {}) },
        work: {
            shelf_drop: { ...e.work.shelf_drop, ...(p.work?.shelf_drop ?? {}) },
            picking: mergeTypes(e.work.picking, p.work?.picking),
            sorting: { pick_and_sort: { ...e.work.sorting.pick_and_sort, ...(p.work?.sorting?.pick_and_sort ?? {}) } },
            dispatch: mergeTypes(e.work.dispatch, p.work?.dispatch),
            flagged: { ...e.work.flagged, ...(p.work?.flagged ?? {}) },
            orders_open: p.work?.orders_open ?? 0,
        },
        rates: {
            shelf_drop: { ...e.rates.shelf_drop, ...(p.rates?.shelf_drop ?? {}) },
            picking: mergeTypes(e.rates.picking, p.rates?.picking),
            sorting: { pick_and_sort: { ...e.rates.sorting.pick_and_sort, ...(p.rates?.sorting?.pick_and_sort ?? {}) } },
            dispatch: mergeTypes(e.rates.dispatch, p.rates?.dispatch),
        },
        active: {
            shelf_drop: p.active?.shelf_drop ?? 0,
            picking: mergeCounts(e.active.picking, p.active?.picking),
            sorting: { pick_and_sort: p.active?.sorting?.pick_and_sort ?? 0 },
            dispatch: mergeCounts(e.active.dispatch, p.active?.dispatch),
        },
    };
};

export const fetchPlanner = async (warehouse: string, scope: PlannerScope = "due"): Promise<PlannerPayload> =>
    normalisePlanner(await fetchActivity<Partial<PlannerPayload>>("/admin/dashboard/planner", warehouse, { scope }));

/**
 * Planner › Pick Advisor (Alex, 2026-10-07): per warehouse, what the
 * advisor is recommending a free Low-queue picker right now (the
 * selector's own `advisor` field, through the Pick Queue's cache), the
 * live stage state it decided from, the derived sort load / pack
 * runway, and how today's wave starts went against the advice.
 */
export interface PickAdvisorPick {
    pickType: "light" | "pick_and_sort" | "large";
    sortStation: string;
    includePutWall: boolean;
    waveSize: string;
    orders: number;
    /** Paired waves: 2 = build it as a joined pick. */
    segments?: number;
    /** Paired waves: join this open half (wave id) rather than build a new wave. */
    join?: number;
    segment_no?: number;
}
export interface PickAdvisorRecommendation {
    rule: string;
    pick: PickAdvisorPick | null;
    reason?: Record<string, unknown>;
    projections?: Record<string, number | string | boolean | null | undefined>;
    clocks?: Record<string, string | null>;
    pools?: Record<string, number | boolean>;
    state_at?: string;
    generated_at?: string;
}
export interface PickAdvisorSortWave {
    wave_id: number;
    station: string;
    items: number;
    orders: number;
    completed_at?: string | null;
    remaining?: number;
    /** Paired waves: which half this is; null on a solo wave. */
    segment_no?: number | null;
    segment_count?: number | null;
}
export interface PickAdvisorInflightWave {
    wave_id: number;
    type: string;
    status: string;
    tier: string | null;
    orders: number;
    items: number;
    picked: number;
    remaining_items: number;
    lands_at: string | null;
    picker_uid: number | null;
    started_at: string | null;
    /** Paired waves fold into one row keyed by the lead wave: its halves, member ids, halves nobody is on yet. */
    segments?: number;
    members?: number[];
    halves_waiting?: number;
}
export interface PickAdvisorStart {
    ts: string;
    wave_id: number;
    picker_uid: number | null;
    picker_name: string | null;
    pick_type: string | null;
    tier: string | null;
    orders: number;
    rule: string | null;
    recommended: string | null;
    followed: boolean | null;
    suggested_only: boolean | null;
    segment_no?: number | null;
    segment_count?: number | null;
}
export interface PickAdvisorWarehouse {
    warehouse: string;
    advisor: PickAdvisorRecommendation | null;
    /** The selector read failed — `advisor` is null for that reason, not because the advisor is off. */
    advisor_error: boolean;
    load: { sort_load_min: number; pack_runway_min: number | null };
    state: {
        sort: {
            waiting: PickAdvisorSortWave[];
            busy: PickAdvisorSortWave[];
            sorters_now: number;
            sorters_expected: number;
            typical_sorters: number;
            wall_slots: number;
            wall_slots_free: number;
            first_sort_today: string | null;
        };
        inflight: PickAdvisorInflightWave[];
        pack: {
            ready_by_type: Record<string, number>;
            ready_units_by_type: Record<string, number>;
            ready_orders: number;
            packers_now: number;
            first_pack_today: string | null;
        };
        pickers_now: number;
    };
    state_at: string;
    today: {
        starts: number;
        advised: number;
        followed: number;
        suggested_only: number;
        by_rule: Array<{ rule: string; advised: number; followed: number }>;
        recent: PickAdvisorStart[];
    };
}
export interface PickAdvisorPayload extends Snapshotted {
    generated_at: string;
    /** The PICK_ADVISOR_ENABLED kill switch. */
    enabled: boolean;
    warehouses: PickAdvisorWarehouse[];
}

export const EMPTY_PICK_ADVISOR: PickAdvisorPayload = { generated_at: "", enabled: true, warehouses: [] };

const emptyAdvisorWarehouse = (): PickAdvisorWarehouse => ({
    warehouse: "",
    advisor: null,
    advisor_error: false,
    load: { sort_load_min: 0, pack_runway_min: null },
    state: {
        sort: { waiting: [], busy: [], sorters_now: 0, sorters_expected: 0, typical_sorters: 0, wall_slots: 0, wall_slots_free: 0, first_sort_today: null },
        inflight: [],
        pack: { ready_by_type: {}, ready_units_by_type: {}, ready_orders: 0, packers_now: 0, first_pack_today: null },
        pickers_now: 0,
    },
    state_at: "",
    today: { starts: 0, advised: 0, followed: 0, suggested_only: 0, by_rule: [], recent: [] },
});

// Every block merged over its empty shape, so a BE a build behind
// never crashes the tab.
const normaliseAdvisorWarehouse = (raw: Partial<PickAdvisorWarehouse>): PickAdvisorWarehouse => {
    const e = emptyAdvisorWarehouse();
    return {
        ...e,
        ...raw,
        advisor: raw.advisor && typeof raw.advisor === "object" ? raw.advisor : null,
        advisor_error: raw.advisor_error === true,
        load: { ...e.load, ...(raw.load ?? {}) },
        state: {
            sort: { ...e.state.sort, ...(raw.state?.sort ?? {}), waiting: raw.state?.sort?.waiting ?? [], busy: raw.state?.sort?.busy ?? [] },
            inflight: Array.isArray(raw.state?.inflight) ? raw.state!.inflight : [],
            pack: { ...e.state.pack, ...(raw.state?.pack ?? {}) },
            pickers_now: raw.state?.pickers_now ?? 0,
        },
        today: { ...e.today, ...(raw.today ?? {}), by_rule: raw.today?.by_rule ?? [], recent: raw.today?.recent ?? [] },
    };
};

export const normalisePickAdvisor = (raw: Partial<PickAdvisorPayload> | null | undefined): PickAdvisorPayload => ({
    ...EMPTY_PICK_ADVISOR,
    ...(raw ?? {}),
    enabled: raw?.enabled !== false,
    warehouses: Array.isArray(raw?.warehouses) ? raw!.warehouses.map(normaliseAdvisorWarehouse) : [],
});

export const fetchPickAdvisor = async (warehouse: string): Promise<PickAdvisorPayload> =>
    normalisePickAdvisor(await fetchActivity<Partial<PickAdvisorPayload>>("/admin/dashboard/pick-advisor", warehouse));

/**
 * Team KPIs over a date range (inclusive London dates, YYYY-MM-DD).
 * on_time = orders DUE in the range on days that have PASSED (business-
 * day due date: 13:00 cut-off, Sat/Sun roll to Monday, standard orders
 * due Monday ship Tuesday); a day is scored from its 15:30 dispatch
 * cut-off — scoring earlier would count only the already-shipped
 * orders and flatter. An order shipped on a day BEFORE its due day
 * counts on the day it shipped (on time, and in `early`), so Monday's
 * figure carries the standard orders the site shipped that day rather
 * than inflating Tuesday's. rate_pct null when nothing was due.
 * time_to_ship = mean placed→packed hours over orders PACKED in the
 * range. daily = one entry per date in the range, zero-filled.
 */
export interface TeamKpiOnTimeDay { date: string; total: number; on_time: number; early: number; }
export interface TeamKpiShipDay { date: string; orders: number; avg_hours: number | null; }
export interface TeamKpis extends Snapshotted {
    from: string;
    to: string;
    today: string;
    /** "15:30" — the dispatch cut-off the on-time rule scores against. */
    dispatch_cutoff: string;
    on_time: { rate_pct: number | null; on_time: number; total: number; early: number; daily: TeamKpiOnTimeDay[] };
    time_to_ship: { avg_hours: number | null; orders: number; daily: TeamKpiShipDay[] };
}

export const EMPTY_TEAM_KPIS: TeamKpis = {
    from: "", to: "", today: "", dispatch_cutoff: "15:30",
    on_time: { rate_pct: null, on_time: 0, total: 0, early: 0, daily: [] },
    time_to_ship: { avg_hours: null, orders: 0, daily: [] },
};

/**
 * Weekly KPIs: the Daily section consolidated over the working week
 * (Monday → Saturday), one bucket per day in `daily` (index 0 = Monday;
 * days still ahead are 0). Headline rates use the same session basis as
 * the daily cards, summed over the week's days. Comparison figures are
 * the same point last week (last Monday → same weekday + time).
 */
export interface TeamWeekDept {
    units: number;
    units_per_hour: number | null;
    daily: number[];
    last_week_units: number;
}
export interface TeamWeek extends Snapshotted {
    week: {
        monday: string;
        /** Mon..Sat as YYYY-MM-DD. */
        days: string[];
        today: string;
        /** 0..5 while the week is in progress; null on Sunday. */
        today_index: number | null;
        generated_at: string;
    };
    site: {
        orders_dispatched: number;
        orders_due: number;
        orders_dispatched_express: number;
        orders_due_express: number;
        orders_dispatched_standard: number;
        orders_due_standard: number;
        orders_dispatched_same_point_last_week: number;
        /** On-time / late split of orders_dispatched (business-day due rule). */
        orders_on_time: number;
        orders_late: number;
        orders_on_time_express: number;
        orders_on_time_standard: number;
        cutoff: string;
        layout?: "picking" | "sorter";
    };
    goods_in: TeamWeekDept & { hot_units: number; cold_units: number; sourcing_units?: number };
    putaway: {
        totes: number;
        units: number;
        hot_totes: number;
        cold_totes: number;
        hot_units: number;
        cold_units: number;
        /** Distinct SKUs put away this week / new-to-the-warehouse SKUs among them. */
        skus: number;
        new_skus: number;
        /** Totes per day. */
        daily: number[];
        /** Units per day (same index as `daily`) — for the chart tooltip. */
        daily_units: number[];
        last_week_totes: number;
    };
    shelf_drop: TeamWeekDept & { awaiting_putaway: number };
    forecast_drop: TeamWeekDept & { awaiting_putaway: number };
    picking: TeamWeekDept;
    sorting: TeamWeekDept & { orders_sorted: number };
    dispatch: TeamWeekDept & { orders: number };
    /** Sorter layout only (Newport, Poland). */
    sorter_1?: TeamWeekDept;
    sorter_2?: TeamWeekDept;
    pallets_created?: TeamWeekDept & { red: number; yellow: number; grey: number; black: number };
}

const EMPTY_WEEK_DEPT: TeamWeekDept = { units: 0, units_per_hour: null, daily: Array<number>(6).fill(0), last_week_units: 0 };
export const EMPTY_TEAM_WEEK: TeamWeek = {
    week: { monday: "", days: [], today: "", today_index: null, generated_at: "" },
    site: {
        orders_dispatched: 0, orders_due: 0,
        orders_dispatched_express: 0, orders_due_express: 0,
        orders_dispatched_standard: 0, orders_due_standard: 0,
        orders_dispatched_same_point_last_week: 0,
        orders_on_time: 0, orders_late: 0, orders_on_time_express: 0, orders_on_time_standard: 0,
        cutoff: "13:00",
    },
    goods_in: { ...EMPTY_WEEK_DEPT, hot_units: 0, cold_units: 0 },
    putaway: {
        totes: 0, units: 0, hot_totes: 0, cold_totes: 0, hot_units: 0, cold_units: 0,
        skus: 0, new_skus: 0,
        daily: Array<number>(6).fill(0), daily_units: Array<number>(6).fill(0), last_week_totes: 0,
    },
    shelf_drop: { ...EMPTY_WEEK_DEPT, awaiting_putaway: 0 },
    forecast_drop: { ...EMPTY_WEEK_DEPT, awaiting_putaway: 0 },
    picking: { ...EMPTY_WEEK_DEPT },
    sorting: { ...EMPTY_WEEK_DEPT, orders_sorted: 0 },
    dispatch: { ...EMPTY_WEEK_DEPT, orders: 0 },
};

export const fetchTeamWeek = (warehouse: string): Promise<TeamWeek> =>
    fetchActivity<TeamWeek>("/admin/dashboard/team-week", warehouse);

export const fetchTeamKpis = (warehouse: string, from: string, to: string): Promise<TeamKpis> =>
    fetchActivity<TeamKpis>("/admin/dashboard/team-kpis", warehouse, { from, to });

/**
 * Orders Shipped by date (Alex, 2026-10-23): the Poland Team tile. One
 * bucket per London date of the range — the Jobalots orders PLACED that
 * day (`total`; cancelled and never-packed refunds excluded,
 * consolidation children folded into their master) and how many of them
 * have shipped so far (`shipped` = packed), with `pct` (null when the day
 * has no orders). Range totals alongside. Default range on the BE = the
 * last 7 days, today included; max 92 days.
 */
export interface ShippedByDateDay {
    date: string;
    total: number;
    shipped: number;
    pct: number | null;
}
export interface ShippedByDate extends Snapshotted {
    from: string;
    to: string;
    today: string;
    channel: string;
    total: number;
    shipped: number;
    pct: number | null;
    daily: ShippedByDateDay[];
}

export const EMPTY_SHIPPED_BY_DATE: ShippedByDate = {
    from: "", to: "", today: "", channel: "Jobalots", total: 0, shipped: 0, pct: null, daily: [],
};

export const fetchShippedByDate = (warehouse: string, from: string, to: string): Promise<ShippedByDate> =>
    fetchActivity<ShippedByDate>("/admin/dashboard/shipped-by-date", warehouse, { from, to });
